import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import Toast from 'shared/components/Toast';import PageHeader from '../components/PageHeader';
import adminService from '../services/adminService';
import { AdminUserFilters } from '../types/admin';
import { User, UserRole } from '../types/auth';
import styles from './AdminUsersList.module.css';

const AdminUsersList: React.FC = () => {  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Filter state
  const [filters, setFilters] = useState<AdminUserFilters>({
    page: 1,
    limit: 10,
  });

  const [searchTerm, setSearchTerm] = useState('');

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams: AdminUserFilters = {
        ...filters,
        page: currentPage,
        search: searchTerm || undefined,
      };

      const response = await adminService.getAllUsers(filterParams);

      setUsers(response.users);
      setTotalPages(response.pagination.totalPages);
      setTotalUsers(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [currentPage, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        setCurrentPage(1); // Reset to first page on search
        loadUsers();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (
    filterKey: keyof AdminUserFilters,
    value: any
  ) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value === '' ? undefined : value,
    }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 100,
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const getRoleName = (role: UserRole): string => {
    const roleMap: Record<UserRole, string> = {
      researcher: 'Investigador',
      admin: 'Administrador',
      evaluator: 'Evaluador',
    };
    return roleMap[role] || role;
  };

  const getResearcherTypeName = (
    type?: 'consolidated' | 'emerging'
  ): string => {
    if (!type) return '-';
    return type === 'consolidated' ? 'Consolidado' : 'Emergente';
  };

  const formatRun = (run?: string): string => {
    if (!run) return '';
    // Remove any existing formatting
    const cleanRun = run.replace(/[.-]/g, '');

    // Format as XX.XXX.XXX-X
    if (cleanRun.length >= 2) {
      const parts = cleanRun.match(/^(\d{1,2})(\d{3})?(\d{3})?(.)?$/);
      if (parts) {
        let formatted = parts[1];
        if (parts[2]) formatted += '.' + parts[2];
        if (parts[3]) formatted += '.' + parts[3];
        if (parts[4]) formatted += '-' + parts[4];
        return formatted;
      }
    }
    return run;
  };

  const formatLastLogin = (lastLoginAt?: string): string => {
    if (!lastLoginAt) return 'Nunca';

    const date = new Date(lastLoginAt);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return 'Hace un momento';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Hace ${diffInHours} h`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Hace ${diffInDays} días`;

    // For older dates, show the actual date
    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const hasActiveFilters = () => {
    return (
      filters.role ||
      filters.affiliation ||
      filters.unidadAcademica ||
      filters.isActive !== undefined ||
      filters.canApply !== undefined ||
      filters.researcherType ||
      searchTerm
    );
  };

  if (error) {
    return (
      <div className={styles.pageWrapper}>
        <PageHeader title="Gestión de Usuarios" />
        <div className={styles.container}>
          <div className={styles.error}>
            <h2>Error</h2>
            <p>{error}</p>
            <Button onClick={loadUsers} variant="primary">
              Reintentar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <PageHeader
        title="Gestión de Usuarios"
        subtitle="Administración de usuarios del sistema"        actions={
          <>
            <Link to="/admin/dashboard" className={styles.headerLink}>
              Volver
            </Link>
            <Button
              onClick={() => navigate('/admin/users/create')}
              variant="primary"
            >
              Crear Usuario
            </Button>
            <Button onClick={loadUsers} variant="primary" disabled={loading}>
              {loading ? 'Actualizando...' : 'Actualizar'}
            </Button>
          </>
        }
      />

      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionHeaderTop}>
              <div className={styles.sectionHeaderText}>
                <h2>Lista de Usuarios</h2>
                <p>
                  Total: {totalUsers} usuario{totalUsers !== 1 ? 's' : ''}
                </p>
              </div>
              <div className={styles.searchContainer}>
                <input
                  type="text"
                  placeholder="Buscar por nombre o email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className={styles.clearSearchButton}
                    aria-label="Limpiar búsqueda"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className={styles.filters}>
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Rol</label>
                <select
                  value={filters.role || ''}
                  onChange={(e) =>
                    handleFilterChange('role', e.target.value as UserRole)
                  }
                  className={styles.filterSelect}
                >
                  <option value="">Todos</option>
                  <option value="researcher">Investigador</option>
                  <option value="admin">Administrador</option>
                  <option value="evaluator">Evaluador</option>
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Acceso a</label>
                <input
                  type="text"
                  value={filters.affiliation || ''}
                  onChange={(e) =>
                    handleFilterChange('affiliation', e.target.value)
                  }
                  className={styles.filterSelect}
                  placeholder="Buscar afiliación..."
                />
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Unidad Académica</label>
                <input
                  type="text"
                  value={filters.unidadAcademica || ''}
                  onChange={(e) =>
                    handleFilterChange('unidadAcademica', e.target.value)
                  }
                  className={styles.filterSelect}
                  placeholder="Buscar unidad..."
                />
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Puede Postular</label>
                <select
                  value={
                    filters.canApply === undefined
                      ? ''
                      : filters.canApply
                        ? 'true'
                        : 'false'
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      'canApply',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  className={styles.filterSelect}
                >
                  <option value="">Todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Tipo Investigador</label>
                <select
                  value={filters.researcherType || ''}
                  onChange={(e) =>
                    handleFilterChange(
                      'researcherType',
                      e.target.value as 'consolidated' | 'emerging'
                    )
                  }
                  className={styles.filterSelect}
                >
                  <option value="">Todos</option>
                  <option value="consolidated">Consolidado</option>
                  <option value="emerging">Emergente</option>
                </select>
              </div>
            </div>

            <div className={styles.clearFiltersRow}>
              {hasActiveFilters() && (
                <Button
                  onClick={handleClearFilters}
                  variant="secondary"
                  size="sm"
                >
                  Limpiar Filtros
                </Button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className={styles.tableContainer}>
            {loading ? (
              <div className={styles.loadingMessage}>Cargando usuarios...</div>
            ) : users.length === 0 ? (
              <div className={styles.emptyMessage}>
                No se encontraron usuarios con los filtros seleccionados.
              </div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Rol</th>
                    <th>Acceso a</th>
                    <th>Unidad Académica</th>
                    <th>Puede Postular</th>
                    <th>Tipo Investigador</th>
                    <th>Año Doctorado</th>
                    <th>Último Login</th>
                    <th className={styles.actionsColumn}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    return (
                      <tr key={u.id}>
                        <td>
                          <div className={styles.userCell}>
                            <div className={styles.userName}>{u.name}</div>
                            <div className={styles.userEmail}>{u.email}</div>
                            {u.userProfile?.run && (
                              <div className={styles.userRun}>
                                {formatRun(u.userProfile.run)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{getRoleName(u.role)}</td>
                        <td>{u.userProfile?.affiliation || '-'}</td>
                        <td>
                          <div className={styles.unidadCell}>
                            <div>{u.userProfile?.unidadAcademica || '-'}</div>
                            {u.userProfile?.facultadUC && (
                              <div className={styles.unidadLine2}>
                                {u.userProfile.facultadUC}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span>{u.userProfile?.canApply ? 'Sí' : 'No'}</span>
                        </td>
                        <td>
                          {getResearcherTypeName(u.userProfile?.researcherType)}
                        </td>
                        <td>{u.userProfile?.doctorateYear || '-'}</td>
                        <td>{formatLastLogin(u.lastLoginAt)}</td>
                        <td className={styles.actionsColumn}>
                          <Button
                            onClick={() => navigate(`/admin/users/${u.id}`)}
                            variant="secondary"
                            size="sm"
                          >
                            Editar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                variant="secondary"
                size="sm"
              >
                Anterior
              </Button>
              <span className={styles.paginationInfo}>
                Página {currentPage} de {totalPages}
              </span>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                variant="secondary"
                size="sm"
              >
                Siguiente
              </Button>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AdminUsersList;
